import { ColDef, GridReadyEvent, RowDoubleClickedEvent, RowSelectedEvent } from "@ag-grid-community/core";
import {
  Component,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { takeUntil } from "rxjs/operators";
import { IdentityRole, UserWithRoles } from "./identity-data";
import { UserAccountDialogComponent } from "./user-account-dialog.component";
import { UserAccountsService } from "./user-accounts.service";

interface RoleRow {
  role: string;
  assigned: boolean;
  capabilities: string;
}

const ROLE_CAPABILITIES: Record<string, string> = {
  UserAdmin: "Manage user accounts, assign roles, edit initials, lock/unlock users, edit app config",
  AccountAdmin: "Manage accounts, edit job order addresses, credit memos, close/reopen job orders",
  ProductAdmin: "Manage product types, features, feature choices, add-ons, process stations, manufacturers",
  ShippingAdmin: "Mark items as scanned, create vouchers, manage ship prep, bin station override",
  AccountingAdmin: "Print/email invoices & manifests, post to QuickBooks, manage freight charges",
  InventoryAdmin: "Edit EOQ settings, manage inventory scans, adjust inventory levels",
  PullStaff: "Edit pull batches assigned to you",
  PullAdmin: "Create/edit all pull batches, mark items found, manage pull workflow",
  ReceiveAdmin: "Receive purchase orders",
  BinProcessAdmin: "Process add-on bin stations, mark add-on items complete",
  AddonStationAdmin: "Manage add-on station configurations",
  SystemAdmin: "Access system configuration, Proximity API settings, generate API tokens",
};

@Component({
  selector: "app-user-accounts",
  templateUrl: "./user-accounts.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class UserAccountsComponent
  extends DomainBaseComponent
  implements OnInit
{
  userGridOptions!: GridOptionsExt;
  roleGridOptions!: GridOptionsExt;
  users!: UserWithRoles[];
  roles!: IdentityRole[];
  selectedUser!: UserWithRoles;
  roleRows: RoleRow[] = [];
  sessions: Record<string, Date> = {};

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    private userAccountsService: UserAccountsService
  ) {
    super(domainService);
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.userGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onUserGridReady,
      onRowDoubleClicked: this.onRowDoubleClick,
      onRowSelected: this.onUserRowSelected,
      onFirstDataRendered: () => AgFns.selectFirstRow(this.userGridOptions),
    });
    this.roleGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onRoleGridReady,
      cellSelection: false,
    });
    const [data, sessionIds] = await Promise.all([
      this.userAccountsService.getIdentityData(),
      this.userAccountsService.getSessionData()
    ]);
    this.setDisplayRoles(data!.Users);
    this.users = data!.Users;
    this.roles = data!.Roles;

    sessionIds.forEach(id => {
      const [userId, timestamp] = id.split(":");
      this.sessions[userId] = new Date(parseInt(timestamp));
    });
    this.isPageReady = true;
  }

  async onUserGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      { ...AgFns.createButtonProps("", (user: UserWithRoles) => this.editUser(user), { label: "Edit" }), ...AgFns.fixWidth(100) },
      {
        headerName: "First Name",
        field: "FirstName",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(200)
      },
      {
        headerName: "Last Name",
        field: "LastName",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(200)
      },
      {
        headerName: "UserName",
        field: "User.UserName",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(140)
      },
      {
        headerName: "Initials",
        field: "Initials",
        ...AgFns.fixWidth(100)
      },
      { headerName: "Locked Out?", field: "IsLockedOut",...AgFns.fixWidth(140) },
      { headerName: "On Since", field: "OnSince", 
        valueGetter: (params) => this.sessions[params.data.id], 
        valueFormatter: (params) => params.value ? params.value.toLocaleString() : "",
        ...AgFns.fixWidth(170) },
      {
        headerName: "Roles",
        field: "DisplayRoles",
        ...AgFns.flexWidth(200)
      },
    ];
    AgFns.initGrid(event, colDefs);
  }

  async onRoleGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      { headerName: "Role", field: "role", ...AgFns.fixWidth(180), filter: "agTextColumnFilter" },
      { headerName: "Assigned", field: "assigned", ...AgFns.fixWidth(140) },
      { headerName: "Capabilities", field: "capabilities", ...AgFns.flexWidth(400) },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "role", sort: "asc" }]);
  }

  onUserRowSelected(e: RowSelectedEvent) {
    if (!e.node.isSelected()) return;
    this.selectedUser = e.data as UserWithRoles;
    this.buildRoleRows();
  }

  buildRoleRows() {
    const rows = !this.selectedUser ? [] : Object.keys(ROLE_CAPABILITIES).map(roleName => ({
      role: roleName,
      assigned: this.selectedUser.Roles.includes(roleName),
      capabilities: ROLE_CAPABILITIES[roleName],
    }));
    this.roleGridOptions.apiX?.setGridOption("rowData", []);
    this.roleGridOptions.apiX?.setGridOption("rowData", rows);
  }

  setDisplayRoles(users: UserWithRoles[]) {
    users.forEach((u) => (u.DisplayRoles = u.Roles.join(", ")));
  }

  onRowDoubleClick(e: RowDoubleClickedEvent) {
    if (!e.node.isSelected()) {
      return;
    }
    const user = e.data as UserWithRoles;
    this.editUser(user);
  }

  async editUser(user: UserWithRoles, isNew?: boolean) {
    const r = await DialogBaseComponent.open(UserAccountDialogComponent, this, {
      user: user,
      users: this.users,
      roles: this.roles,
      isNew: !!isNew,
    });
    if (r) {
      if (isNew) {
        this.users.push(user);
        this.userGridOptions.apiX?.setGridOption("rowData", this.users);
      }
      if (this.authUser.initials == r.Initials) {
        // TODO: rights and roles may have changed
        // we really should refresh these here.
      }
      this.setDisplayRoles(this.users);
      this.userGridOptions.apiX?.refreshCells({ force: true });
      // Refresh the role detail panel if the edited user is selected
      if (this.selectedUser?.id === r.id) {
        this.selectedUser = r;
        this.buildRoleRows();
      }
    }
    return r;
  }

  async addUser() {
    const user = {
      id: "new",
      IsLockedOut: false,
      Roles: [],
      User: {},
    } as any;
    return this.editUser(user, true);
  }
}
